import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { LayoutDashboard, Users, Car, FileText, Receipt, Settings, Bell, LogOut, Menu, X, Wrench } from 'lucide-react';

const LOGO_URL = "/logo-andeni.png";
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const menuItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/clients', label: 'Clienți', icon: Users },
  { path: '/admin/vehicles', label: 'Vehicule', icon: Car },
  { path: '/admin/orders', label: 'Comenzi', icon: FileText },
  { path: '/admin/invoices', label: 'Facturi', icon: Receipt },
  { path: '/admin/services', label: 'Servicii', icon: Wrench },
  { path: '/admin/settings', label: 'Setări', icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [admin, setAdmin] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('admin_user');
    if (!stored) {
      navigate('/admin/login');
      return;
    }
    setAdmin(JSON.parse(stored));
  }, [navigate]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(API + '/admin/auth/logout', {}, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top navbar */}
      <nav className="bg-slate-900 text-white h-16 fixed top-0 left-0 right-0 z-50">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2">
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Link to="/admin" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="h-10 w-auto brightness-0 invert" />
              <span className="font-semibold hidden sm:block">Admin Panel</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm hidden sm:block">{admin.name}</span>
            <Badge variant="secondary" className="text-xs">{admin.role}</Badge>
            <button onClick={handleLogout} className="p-2 hover:bg-slate-800 rounded-lg" data-testid="admin-logout-btn">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-white border-r border-slate-200 z-40 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-6">
          <Outlet context={{ admin }} />
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)}></div>
      )}
    </div>
  );
}
